import { FC, useMemo, useState } from 'react'
import { Box, Checkbox, ListItemText, MenuItem, OutlinedInput, SelectProps, Typography } from '@mui/material'
import { InstanceCheckedIcon, InstanceUncheckedIcon, SelectDropdownIcon } from 'ui/components/svg'
import { CategoryMenuProps, LibraryFormControl, LibrarySelect, Wrapper } from './librarySelectField.styles'
import SelectSearch from './SelectSearch'
import useSearchLabel from 'hooks/useSearchLabel'
import ResetSearch from 'ui/components/form-components/ResetSearch'
import SortIcon from 'ui/components/svg/SortIcon'
import theme from 'theme'

interface Props extends SelectProps {
  options: string[]
  value: any
  handleChange: any
  handleReset: any
  minWidth?: string
  maxHeight?: string
  noborder?: string
  iscustom?: string
  hideSearch?: boolean
  label?: string
  isSort?: boolean
  version?: string
  field?: string
}

const LibrarySelectField: FC<Props> = ({
  version,
  isSort,
  label,
  options,
  handleChange,
  placeholder,
  maxHeight,
  handleReset,
  noborder,
  minWidth,
  iscustom,
  value,
  hideSearch = false,
  field,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = useState(null)

  const convertedOptions = useMemo(() => {
    return options.map((item) => {
      return {
        label: item,
        name: item
      }
    })
  }, [options])

  const { search, setSearch, labelOptions, resetData, highlightMatch } = useSearchLabel(convertedOptions, handleReset)

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  /* MUI compares the value of a single select with its direct MenuItem children. The options here
   * sit in a scroll container, which keeps the search and reset rows in place, so the comparison
   * always fails and MUI reports an out-of-range value. A single select gets '', because each
   * option applies the change in its own onClick. A multiple select keeps its value, because MUI
   * does not make the comparison for it. */
  const selectValue = rest.multiple ? value : ''

  /* Reads the value prop. The renderValue argument is '' for a single select. */
  const renderSelectedLabel = () => {
    if (Array.isArray(value)) {
      const pruned = value.filter((s: string) => s !== '')
      if (pruned.length === 0) {
        return placeholder
      }
      if (pruned.length === 1) {
        return pruned[0]
      }

      return `${pruned.length} selected`
    }

    return typeof noborder !== 'undefined' ? `${value}` : value
  }

  const svgClassname = useMemo(() => {
    let base = 'MuiSelect-icon MuiSelect-iconOutlined MuiSelect-iconClosed svg-dropdown'
    if (anchorEl) {
      base = base.replaceAll('MuiSelect-iconClosed', 'MuiSelect-iconOpen svg-dropdown')
    } else {
      base = base.replaceAll('MuiSelect-iconOpen', 'MuiSelect-iconClosed svg-dropdown')
    }

    return base
  }, [anchorEl])

  return (
    <Wrapper>
      {label && <Typography className="label-text">{label}</Typography>}
      {isSort && <SortIcon />}
      {/* undefined, not 'false', so React removes the attribute. */}
      <LibraryFormControl
        sx={{ minWidth }}
        issort={isSort ? 'true' : undefined}
        iscustom={iscustom}
        noborder={noborder}
        isselected={
          anchorEl || (placeholder !== 'Sort' && (value.length > 1 || (value.length === 1 && value[0] !== '')))
            ? 'true'
            : 'false'
        }
      >
        <LibrarySelect
          noborder={noborder}
          isselected={
            placeholder !== 'Sort' && (value.length > 1 || (value.length === 1 && value[0] !== '')) ? 'true' : 'false'
          }
          labelId="library-select-label"
          id="library-select"
          iscustom={iscustom}
          displayEmpty
          MenuProps={{
            ...CategoryMenuProps,
            PaperProps: {
              ...CategoryMenuProps.PaperProps,
              sx: {
                ...CategoryMenuProps.PaperProps.sx,
                display: anchorEl ? 'block' : 'none'
              },
              style: {
                ...CategoryMenuProps.PaperProps.style,
                maxHeight: maxHeight ?? CategoryMenuProps.PaperProps.style.maxHeight
              }
            },
            anchorEl,
            open: Boolean(anchorEl),
            onClose: () => setAnchorEl(null)
          }}
          IconComponent={() => <SelectDropdownIcon className={svgClassname} />}
          renderValue={renderSelectedLabel}
          inputProps={{ 'aria-label': 'Without label' }}
          {...rest}
          value={selectValue}
          onChange={handleChange}
          input={<OutlinedInput onMouseDown={handleClick} />}
          sx={{ minWidth }}
        >
          {!hideSearch && <SelectSearch search={search} setSearch={setSearch} placeholder={`Search ${version}`} />}

          <Box className="list-wrapper">
            {labelOptions.map((option) => (
              <MenuItem
                key={option.name}
                value={option.name}
                onClick={() => {
                  if (!rest.multiple) {
                    handleChange({ target: { value: option.name } })
                  } else {
                    let nextValue = [...value]
                    if (nextValue.includes(option.name)) {
                      nextValue = nextValue.filter((c) => c !== option.name)
                    } else {
                      nextValue = [...nextValue, option.name]
                    }
                    handleChange({ target: { value: nextValue } }, field || '')
                  }
                  if (!rest.multiple) {
                    setAnchorEl(null)
                  }
                }}
              >
                {rest.multiple && (
                  <Checkbox
                    checked={value.indexOf(option.name) > -1}
                    icon={<InstanceUncheckedIcon />}
                    checkedIcon={<InstanceCheckedIcon />}
                  />
                )}
                <ListItemText primary={highlightMatch(option.label)} />
              </MenuItem>
            ))}
          </Box>

          <Box className="divider" sx={{ marginTop: `${theme.spacing.md}px !important` }} />
          <ResetSearch resetData={resetData} />
        </LibrarySelect>
      </LibraryFormControl>
    </Wrapper>
  )
}

export default LibrarySelectField
